import {
  WEDLY_CATEGORY_DEFINITIONS,
  matchWedlyDefinitionToHostedCategory,
  isWedlyListingConfigEnabled,
} from '../config/wedlyCategoryListingConfig';

/**
 * Resolve Wedly field-definition for selected Sharetribe category ids.
 * @param {string[]} categoryIds [categoryLevel1, categoryLevel2?, ...]
 * @param {Array} hostedCategories from config.categoryConfiguration.categories
 */
export const getWedlyCategoryDef = (categoryIds, hostedCategories = []) => {
  if (!categoryIds?.length) {
    return null;
  }

  const level1Id = categoryIds[0];

  const byConfigId = WEDLY_CATEGORY_DEFINITIONS.find(c => c.id === level1Id);
  if (byConfigId) {
    return byConfigId;
  }

  if (!hostedCategories?.length) {
    return null;
  }

  return (
    WEDLY_CATEGORY_DEFINITIONS.find(def => {
      const hosted = matchWedlyDefinitionToHostedCategory(def, hostedCategories);
      return hosted?.id === level1Id;
    }) || null
  );
};

/**
 * Fields to show for the selected Sharetribe category levels.
 * @param {string[]} categoryIds
 * @param {Array} hostedCategories
 */
export const getFieldsForCategories = (categoryIds, hostedCategories = []) => {
  const category = getWedlyCategoryDef(categoryIds, hostedCategories);
  return category?.fields || [];
};

/**
 * Required photo slot labels for the selected category.
 * @param {string[]} categoryIds
 * @param {Array} hostedCategories
 */
export const getRequiredImageSlots = (categoryIds, hostedCategories = []) => {
  const category = getWedlyCategoryDef(categoryIds, hostedCategories);
  return category?.requiredImageSlots || [];
};

export const getAllWedlyFieldKeys = () => {
  const keys = new Set();
  WEDLY_CATEGORY_DEFINITIONS.forEach(cat => {
    cat.fields.forEach(f => keys.add(f.key));
  });
  return [...keys];
};

const normalizeSearchValue = value => {
  if (value == null || value === '') {
    return null;
  }
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no';
  }
  return String(value);
};

/**
 * Build normalized searchMetadata for AI search / filtering from form values.
 */
export const buildSearchMetadata = (values, categoryIds, hostedCategories = []) => {
  const fields = getFieldsForCategories(categoryIds, hostedCategories);
  const metadata = {
    style: null,
    theme: null,
    colour: null,
    size: null,
    material: null,
    brand: null,
    occasion: null,
    condition: null,
    weddingStyle: null,
  };

  fields.forEach(field => {
    if (!field.searchFacet) {
      return;
    }
    const raw = values[field.key];
    const normalized = normalizeSearchValue(raw);
    if (normalized != null) {
      metadata[field.searchFacet] = normalized;
    }
  });

  return metadata;
};

/**
 * Pick Wedly field values for publicData from form values.
 */
export const pickWedlyFieldsData = (values, categoryIds, hostedCategories = []) => {
  const activeFields = getFieldsForCategories(categoryIds, hostedCategories);
  const activeKeys = new Set(activeFields.map(f => f.key));
  const allKeys = getAllWedlyFieldKeys();

  return allKeys.reduce((acc, key) => {
    if (!activeKeys.has(key)) {
      return { ...acc, [key]: null };
    }
    const val = values[key];
    if (val === '' || val === undefined) {
      return { ...acc, [key]: null };
    }
    return { ...acc, [key]: val };
  }, {});
};

/**
 * Initial form values for Wedly fields from listing publicData.
 */
export const initialWedlyFieldValues = (publicData, categoryIds, hostedCategories = []) => {
  const activeFields = getFieldsForCategories(categoryIds, hostedCategories);
  return activeFields.reduce((acc, field) => {
    const stored = publicData?.[field.key];
    if (field.type === 'chips' || field.type === 'multiSelect') {
      return { ...acc, [field.key]: Array.isArray(stored) ? stored : [] };
    }
    if (field.type === 'toggle') {
      return { ...acc, [field.key]: stored === true };
    }
    return { ...acc, [field.key]: stored != null ? stored : '' };
  }, {});
};

/**
 * Format a Wedly field value for display on ListingPage.
 * @param {Object} field field definition from wedlyCategoryListingConfig
 * @param {*} value stored publicData value
 * @param {Object} intl react-intl instance
 */
export const formatWedlyFieldDisplayValue = (field, value, intl) => {
  if (value == null || value === '') {
    return null;
  }

  const yesLabel = intl.formatMessage({ id: 'ListingPage.detailYes' });
  const noLabel = intl.formatMessage({ id: 'ListingPage.detailNo' });

  switch (field.type) {
    case 'toggle':
      return value === true ? yesLabel : value === false ? noLabel : null;
    case 'radio':
    case 'select': {
      const option = field.options?.find(o => o.value === value);
      return option?.label || String(value);
    }
    case 'multiSelect':
    case 'chips': {
      if (!Array.isArray(value) || value.length === 0) {
        return null;
      }
      return value
        .map(v => field.options?.find(o => o.value === v)?.label || v)
        .filter(Boolean)
        .join(', ');
    }
    case 'number':
      return String(value);
    case 'textarea':
      return String(value);
    case 'text':
    default:
      return String(value);
  }
};

/**
 * Detail rows for ListingPage ({ key, label, value }).
 */
export const getWedlyListingDetailRows = (publicData, categoryIds, hostedCategories, intl) => {
  const fields = getFieldsForCategories(categoryIds, hostedCategories);

  return fields
    .map(field => {
      const displayValue = formatWedlyFieldDisplayValue(field, publicData?.[field.key], intl);
      if (displayValue == null || displayValue === '') {
        return null;
      }
      return { key: field.key, label: field.label, value: displayValue };
    })
    .filter(Boolean);
};

/**
 * Edit Listing uses Wedly category fields only (not Sharetribe listing-fields inputs).
 */
export const EDIT_LISTING_USE_WEDLY_FIELDS_ONLY = true;

export { isWedlyListingConfigEnabled };
